/**
 * Small, dependency-free string hash for our own derived signals (WebGL report/image/ext).
 * FNV-1a 64-bit → hex. Not cryptographic — just a stable, compact fingerprint of an input.
 */
export function hashString(input: string): string {
  // FNV-1a 64-bit using BigInt for correctness across the full value range.
  const FNV_OFFSET = 0xcbf29ce484222325n;
  const FNV_PRIME = 0x100000001b3n;
  const MASK = 0xffffffffffffffffn;

  let hash = FNV_OFFSET;
  for (let i = 0; i < input.length; i++) {
    hash ^= BigInt(input.charCodeAt(i) & 0xff);
    hash = (hash * FNV_PRIME) & MASK;
    // mix high bytes of multi-byte chars too
    const hi = input.charCodeAt(i) >> 8;
    if (hi) {
      hash ^= BigInt(hi);
      hash = (hash * FNV_PRIME) & MASK;
    }
  }
  return hash.toString(16).padStart(16, "0");
}

/** Hash an arbitrary value by stable JSON stringification. */
export function hashValue(value: unknown): string {
  return hashString(stableStringify(value));
}

function stableStringify(value: unknown): string {
  if (value === null || typeof value !== "object") {
    return JSON.stringify(value);
  }
  if (Array.isArray(value)) {
    return "[" + value.map(stableStringify).join(",") + "]";
  }
  const keys = Object.keys(value as Record<string, unknown>).sort();
  return (
    "{" +
    keys
      .map((k) => JSON.stringify(k) + ":" + stableStringify((value as Record<string, unknown>)[k]))
      .join(",") +
    "}"
  );
}
